import { useEffect, useState } from "react";
import { recordManualDonation, type DonationMethod } from "../actions";
import { IcCheck, IcWallet } from "./icons";

/**
 * Recording money that arrived outside the payment provider.
 *
 * Cash in an envelope, a transfer, a cheque at the door. Until now `donations`
 * could only be written by a Grow callback or the historical import, so the most
 * ordinary donation a Chabad house receives — somebody handing over notes — had
 * nowhere to go.
 *
 * ## The form asks for four things and defaults three of them
 *
 * Amount is the only field that cannot be guessed. The date defaults to today
 * and moves back, because money is usually recorded after the fact rather than
 * as it is handed over. The method defaults to cash, which is what it almost
 * always is. Everything else is optional and stays out of the way.
 */

const METHODS: { code: DonationMethod; label: string }[] = [
  { code: "cash", label: "מזומן" },
  { code: "transfer", label: "העברה" },
  { code: "cheque", label: "צ׳ק" },
  { code: "card", label: "אשראי" },
  { code: "other", label: "אחר" },
];

/** `yyyy-mm-dd` in local time — `toISOString` would shift the date across
 *  midnight for anybody east of Greenwich, which is everybody here. */
function todayValue(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function AddDonationSheet({
  personId,
  personName,
  onClose,
}: {
  personId: string;
  personName: string;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<DonationMethod>("cash");
  const [when, setWhen] = useState(todayValue);
  const [purpose, setPurpose] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  /**
   * Shekels in, agorot out, rounded once.
   *
   * Money is held as an integer everywhere in this system precisely so that
   * 0.1 + 0.2 never appears in a total. The rounding happens here, at the one
   * boundary where a human types a decimal.
   */
  const minor = (() => {
    const n = Number(amount.replace(/[^\d.]/g, ""));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.round(n * 100);
  })();

  async function save() {
    if (minor === null) {
      setErr("כתבו סכום.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await recordManualDonation({
        personId,
        amountMinor: minor,
        // Midday local, so the stored instant lands on the day that was picked
        // whichever way the timezone rounds.
        collectedAt: new Date(`${when}T12:00:00`).toISOString(),
        method,
        purpose,
        note,
      });
      setDone(`רשמנו ${(minor / 100).toLocaleString("he-IL")} ₪`);
      setTimeout(onClose, 1100);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={busy ? undefined : onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label="הוספת תרומה">
        <div className="sheet-grip" />

        {done ? (
          <div className="qa-done">
            <div className="qa-done-mark">
              <IcCheck size={26} />
            </div>
            <p className="name">{done}</p>
            <p className="muted">בכרטיס של {personName}</p>
          </div>
        ) : (
          <>
            <div className="sheet-head">
              <span className="rail-ico tint-amber">
                <IcWallet size={18} />
              </span>
              <div className="grow">
                <p className="name" style={{ marginBottom: 0 }}>
                  הוספת תרומה
                </p>
                <p className="muted">{personName}</p>
              </div>
            </div>

            <div className="sheet-body">
              <label className="field-label" htmlFor="don-amount">
                סכום בשקלים
              </label>
              <input
                id="don-amount"
                className="field-input amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder="0"
                autoFocus
                disabled={busy}
              />

              <label className="field-label">איך שולם</label>
              <div className="chips" style={{ marginBottom: 12 }}>
                {METHODS.map((m) => (
                  <button
                    key={m.code}
                    type="button"
                    className={method === m.code ? "tagchip tagchip-on" : "tagchip"}
                    aria-pressed={method === m.code}
                    onClick={() => setMethod(m.code)}
                    disabled={busy}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <label className="field-label" htmlFor="don-when">
                מתי
              </label>
              <input
                id="don-when"
                className="field-input"
                type="date"
                value={when}
                max={todayValue()}
                onChange={(e) => setWhen(e.target.value)}
                disabled={busy}
              />

              <label className="field-label" htmlFor="don-purpose">
                למה התרומה <span className="faint">(לא חובה)</span>
              </label>
              <input
                id="don-purpose"
                className="field-input"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="בית הכנסת, כפרות, סתם…"
                disabled={busy}
              />

              <label className="field-label" htmlFor="don-note">
                הערה <span className="faint">(לא חובה)</span>
              </label>
              <textarea
                id="don-note"
                className="field-input"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="מסר ביד בבית הכנסת…"
                disabled={busy}
              />

              {/*
                Said plainly rather than discovered later. The donor screen splits
                giving into provider-confirmed and everything else, and a typed
                amount belongs on the second side however honest it is. Somebody
                reconciling against the bank needs to know which is which.
              */}
              <p className="faint" style={{ marginTop: 10 }}>
                תרומה שמוסיפים ידנית נרשמת כ״לא מאומתת״. היא נספרת בסך התרומות, אבל בנפרד ממה
                שחברת הסליקה אישרה.
              </p>

              {err && (
                <div className="notice error" style={{ marginTop: 10 }}>
                  {err}
                </div>
              )}
            </div>

            <div className="btn-row">
              <button className="btn" onClick={onClose} disabled={busy}>
                ביטול
              </button>
              <button className="btn btn-primary" onClick={save} disabled={busy || minor === null}>
                {busy ? "שומרים…" : minor === null ? "הוסיפו" : `הוסיפו ${(minor / 100).toLocaleString("he-IL")} ₪`}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
